// Settlement & disbursement — PI Platform outline v2 §1.10.
//
// The last card on the Compliance tab, below liens and costs, because
// it consumes both.
//
// The whole UI turns on one distinction the user has to feel, not read
// about in a tooltip:
//
//   A DRAFT is live. It moves when a lien is renegotiated or a cost is
//   added, and it shows the checklist of what is standing in the way.
//
//   A LOCKED STATEMENT is frozen. It is what the client signed, and it
//   keeps saying what it said even after the underlying data changes.
//
// So drafts and statements look different on purpose, and the lock
// button is not available until the checklist is clear — with an
// explicit override that records what it overrode.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);

const OFFER_LABELS = {
  demand_sent: "Demand sent", offer_received: "Offer received",
  counter_sent: "Counter sent", accepted: "Accepted", rejected: "Rejected",
};

const STATUS_LABELS = {
  draft: "Draft", client_approved: "Client approved", executed: "Executed",
};

const LINE_LABELS = {
  attorney_fee: "Attorney's fee", case_cost: "Case cost",
  lien: "Lien", client_net: "Client", other: "Other",
};

export function SettlementCard({ matterId, showNotification }) {
  const [settlements, setSettlements] = useState(null);
  const [statements, setStatements] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newParty, setNewParty] = useState("");
  const [newMinor, setNewMinor] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        apiFetch(`/api/matters/${matterId}/settlements`),
        apiFetch(`/api/matters/${matterId}/disbursement-statements`),
      ]);
      setSettlements(s.data || []);
      setStatements(st.data || []);
    } catch (e) {
      showNotification?.(`Settlements failed to load: ${e.message}`, "error");
      setSettlements([]);
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const primary = (settlements || [])[0] || null;

  const loadDraft = useCallback(async () => {
    if (!primary || !primary.gross_amount) { setDraft(null); return; }
    try {
      const p = new URLSearchParams({ settlement_id: String(primary.id) });
      setDraft(await apiFetch(`/api/matters/${matterId}/disbursement-draft?${p}`));
    } catch (e) {
      setDraft(null);
      showNotification?.(`Draft failed: ${e.message}`, "warning");
    }
  }, [matterId, primary, showNotification]);

  useEffect(() => { loadDraft(); }, [loadDraft]);

  const addSettlement = async () => {
    try {
      await apiFetch(`/api/matters/${matterId}/settlements`, {
        method: "POST",
        body: JSON.stringify({
          settled_with: newParty.trim() || null,
          requires_court_approval: newMinor,
        }),
      });
      setNewParty(""); setNewMinor(false);
      await load();
    } catch (e) { showNotification?.(`Couldn't create: ${e.message}`, "error"); }
  };

  const patchSettlement = async (id, changes) => {
    try {
      await apiFetch(`/api/settlements/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
      await load();
    } catch (e) { showNotification?.(`Update failed: ${e.message}`, "error"); }
  };

  const addOffer = async (direction) => {
    const raw = window.prompt(`${OFFER_LABELS[direction]} — amount:`);
    if (!raw) return;
    try {
      await apiFetch(`/api/settlements/${primary.id}/offers`, {
        method: "POST", body: JSON.stringify({ direction, amount: Number(raw) }),
      });
      await load();
    } catch (e) { showNotification?.(`Couldn't record: ${e.message}`, "error"); }
  };

  const lock = async (force) => {
    if (force && !window.confirm(
      "Lock this statement over the outstanding items?\n\n" +
      "They will be recorded on the statement itself, so it shows what was " +
      "overridden and by whom.")) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/api/matters/${matterId}/disbursement-statements`, {
        method: "POST",
        body: JSON.stringify({ settlement_id: primary.id, force: !!force }),
      });
      if (r.ok) {
        showNotification?.(`Statement v${r.statement.version} locked.`, "success");
        await load();
      } else {
        showNotification?.("Still blocked — see the checklist.", "warning");
      }
    } catch (e) { showNotification?.(`Lock failed: ${e.message}`, "error"); }
    setBusy(false);
  };

  const advance = async (id, to_status) => {
    const label = to_status === "executed"
      ? "Mark this statement executed? This records that the money has gone out."
      : "Record the client's approval of this version?";
    if (!window.confirm(label)) return;
    try {
      await apiFetch(`/api/disbursement-statements/${id}/advance`, {
        method: "POST", body: JSON.stringify({ to_status }),
      });
      await load();
    } catch (e) { showNotification?.(`${e.message}`, "error"); }
  };

  if (settlements === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
          "Loading settlement…")));
  }

  const inp = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };

  return React.createElement("div", { style: cardStyle },
    React.createElement("h2", { style: h2Style }, "Settlement & Disbursement"),

    // ── No settlement yet ───────────────────────────────────
    !primary && React.createElement("div", { style: { marginTop: "10px" } },
      React.createElement("p", { style: { fontSize: "13px", color: C.textMuted, marginBottom: "10px" } },
        "No settlement recorded. Open one to track offers and build the disbursement."),
      React.createElement("div", { style: { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" } },
        React.createElement("input", {
          type: "text", value: newParty, placeholder: "Settling with (carrier or defendant)",
          onChange: e => setNewParty(e.target.value), style: { ...inp, minWidth: "260px" },
        }),
        React.createElement("label", {
          title: "Minors' settlements — and wrongful death in most states — need court approval before money moves.",
          style: { display: "flex", gap: "5px", alignItems: "center", fontSize: "12px", color: C.textMuted, cursor: "pointer" },
        },
          React.createElement("input", { type: "checkbox", checked: newMinor,
            onChange: e => setNewMinor(e.target.checked) }),
          "Needs court approval"),
        React.createElement("button", {
          onClick: addSettlement,
          style: { padding: "8px 16px", borderRadius: "8px", border: "none", background: C.gold,
                   color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer" },
        }, "Open settlement"))),

    // ── The settlement ──────────────────────────────────────
    primary && React.createElement("div", null,
      React.createElement("div", {
        style: { display: "flex", gap: "22px", flexWrap: "wrap", padding: "12px 14px",
                 background: C.warmWhite, borderRadius: "8px", margin: "12px 0 14px" },
      },
        _stat("Settling with", primary.settled_with || "—"),
        _stat("Gross", money(primary.gross_amount)),
        _stat("Funds cleared", dateOnly(primary.funds_cleared_date) || "not yet"),
        primary.requires_court_approval
          ? _stat("Court approval", dateOnly(primary.court_approval_date) || "required, not recorded")
          : null,
      ),

      // Negotiation history
      React.createElement("div", { style: { marginBottom: "12px" } },
        (primary.offers || []).length > 0 && React.createElement("div", {
          style: { display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px" },
        }, ...primary.offers.map(o => React.createElement("div", {
          key: o.id, style: { display: "flex", gap: "10px", fontSize: "12px", color: C.textDark },
        },
          React.createElement("span", { style: { color: C.textLight, minWidth: "88px" } },
            dateOnly(o.occurred_at) || ""),
          React.createElement("span", { style: { fontWeight: 600, minWidth: "130px" } },
            OFFER_LABELS[o.direction] || o.direction),
          React.createElement("span", null, money(o.amount))))),
        React.createElement("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap" } },
          _quiet("Demand sent", () => addOffer("demand_sent")),
          _quiet("Offer received", () => addOffer("offer_received")),
          _quiet("Counter sent", () => addOffer("counter_sent")),
          _quiet("Accept offer", () => addOffer("accepted")),
          !primary.funds_cleared_date && _quiet("Funds cleared", () =>
            patchSettlement(primary.id, {
              funds_cleared_date: new Date().toISOString(), status: "funds_cleared" })),
          primary.requires_court_approval && !primary.court_approval_date && _quiet(
            "Court approved", () =>
              patchSettlement(primary.id, { court_approval_date: new Date().toISOString() })),
        )),

      // ── Draft + blockers ──────────────────────────────────
      draft && React.createElement("div", {
        style: { marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${C.warmGray}60` },
      },
        React.createElement("h3", { style: h3Style }, "Draft disbursement"),
        React.createElement("div", {
          style: { fontSize: "11px", color: C.textMuted, margin: "2px 0 10px" },
        }, "Live — moves as liens and costs change, until it is locked."),

        React.createElement("div", { style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px", overflow: "hidden" } },
          ...draft.lines.map((l, i) => _row(
            `${LINE_LABELS[l.line_type] || l.line_type}${
              l.line_type === "client_net" || l.line_type === "attorney_fee" ? "" : ` — ${l.recipient_name}`}`,
            money(l.amount), l.line_type === "client_net", i))),

        (draft.blockers || []).length > 0
          ? React.createElement("div", { style: { marginTop: "10px" } },
              React.createElement("div", {
                style: { fontSize: "12px", fontWeight: 700, color: C.navy, marginBottom: "6px" },
              }, `${draft.blockers.length} thing${draft.blockers.length === 1 ? "" : "s"} to clear before this can be locked`),
              React.createElement("ul", {
                style: { margin: 0, paddingLeft: "18px", fontSize: "12px", color: C.textMuted, lineHeight: 1.6 },
              }, ...draft.blockers.map((b, i) => React.createElement("li", { key: i }, b))),
              React.createElement("button", {
                onClick: () => lock(true), disabled: busy,
                style: { marginTop: "10px", padding: "7px 14px", borderRadius: "8px",
                         border: `1px solid ${C.warmGray}`, background: "#fff", color: C.navy,
                         fontSize: "12px", fontWeight: 600, cursor: busy ? "wait" : "pointer" },
              }, "Lock anyway, recording these"))
          : React.createElement("button", {
              onClick: () => lock(false), disabled: busy,
              style: { marginTop: "12px", padding: "9px 18px", borderRadius: "8px", border: "none",
                       background: busy ? C.warmGray : C.gold, color: "#fff",
                       fontSize: "13px", fontWeight: 700, cursor: busy ? "wait" : "pointer" },
            }, busy ? "Locking…" : "Lock this statement"),
      ),
    ),

    // ── Locked statements ───────────────────────────────────
    statements.length > 0 && React.createElement("div", {
      style: { marginTop: "16px", paddingTop: "12px", borderTop: `1px solid ${C.warmGray}60` },
    },
      React.createElement("h3", { style: h3Style }, "Locked statements"),
      React.createElement("div", {
        style: { fontSize: "11px", color: C.textMuted, margin: "2px 0 10px" },
      }, "Frozen at lock time. These do not change when liens or costs change afterwards."),
      ...statements.map(st => React.createElement("div", {
        key: st.id,
        style: {
          border: `1px solid ${C.warmGray}60`, borderRadius: "8px",
          padding: "11px 13px", marginBottom: "8px",
          background: st.superseded_by_id ? C.warmWhite : "#fff",
          opacity: st.superseded_by_id ? 0.75 : 1,
        },
      },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" } },
          React.createElement("span", { style: { fontSize: "13px", fontWeight: 700, color: C.navy } },
            `Version ${st.version}`),
          React.createElement("span", { style: { fontSize: "11px", color: C.textMuted } },
            STATUS_LABELS[st.status] || st.status),
          st.superseded_by_id && React.createElement("span", {
            style: { fontSize: "11px", color: C.textMuted },
          }, "· superseded"),
          React.createElement("span", { style: { flex: 1 } }),
          React.createElement("span", { style: { fontSize: "13px", fontWeight: 700, color: C.navy } },
            `Client ${money(st.net_to_client)}`),
        ),
        React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "3px" } },
          `Locked ${dateOnly(st.locked_at) || ""}${st.locked_by ? ` by ${st.locked_by}` : ""}`
          + ` · gross ${money(st.gross_amount)} · fee ${money(st.attorney_fee)}`
          + ` · costs ${money(st.costs_total)} · liens ${money(st.liens_total)}`),

        // A statement issued over a gate says so on its own face.
        (st.overridden_blockers || []).length > 0 && React.createElement("div", {
          style: { fontSize: "11px", color: C.navy, marginTop: "6px", fontWeight: 600 },
        }, `Locked over ${st.overridden_blockers.length} outstanding item(s): `
           + st.overridden_blockers.map(b => b.split(".")[0]).join("; ")),

        !st.superseded_by_id && st.status !== "executed" && React.createElement("div", {
          style: { display: "flex", gap: "8px", marginTop: "9px" },
        },
          st.status === "draft" && _quiet("Client approved", () => advance(st.id, "client_approved")),
          st.status === "client_approved" && _quiet("Mark executed", () => advance(st.id, "executed")),
        ),
      )),
    ),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const h3Style = {
  fontSize: "12px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.06em", fontWeight: 600, margin: 0,
};

function _stat(label, value) {
  return React.createElement("div", { key: label },
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    React.createElement("div", { style: { fontSize: "14px", fontWeight: 700, color: "#1B2A4A", marginTop: "2px" } },
      value));
}

function _row(label, value, emphasise, i) {
  return React.createElement("div", {
    key: `${label}-${i}`,
    style: {
      display: "flex", justifyContent: "space-between", padding: "9px 13px",
      borderTop: i === 0 ? "none" : "1px solid #E8E3DC40",
      background: emphasise ? "#FAF8F5" : "#fff",
      fontSize: emphasise ? "14px" : "13px",
      fontWeight: emphasise ? 700 : 400, color: "#1B2A4A",
    },
  },
    React.createElement("span", null, label),
    React.createElement("span", null, value));
}

function _quiet(label, onClick) {
  if (!label) return null;
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer",
    },
  }, label);
}
